import React from 'react';
import moment from 'moment-timezone';
import './subscriptionSummaryListView.module.scss';
import { DetailedDate2, DetailedDateRange, DetailedPeriod } from '../../../../../admin/components/date/TimeRange';
import { MobBr, SpBr } from '../../../../../commonv3/components/atoms/layouts/br';
import { Button as OldButton } from '../../../../../commonv3/components/atoms/buttons/Button';
import { MembershipItemId } from '../../../../../commonv3/types/membershipPlans';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../../routes';
import { SubscriptionSummary } from '../../../../types/subscriptionSummary';
import { usePlanOperationConfig } from '../../../../../commonv3/hooks/usePlanOperationConfig';
import { Button } from '@sheinc/shelikes-design-system';
import { TokenType } from '../../../../../commonv3/types/tokens';

const SubscriptionSummaryRow = ({
  title,
  children,
  className = undefined,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div styleName="subscription-summary-row" className={className}>
    <div styleName="title">{title}</div>
    <div styleName="body">{children}</div>
  </div>
);

type Props = {
  summary: SubscriptionSummary;
  loading?: boolean;
  isWithdrawalSet: boolean;
  onUpdateRequest?: (itemId: MembershipItemId) => void;
  onUpdateCancelRequest?: (originalItemId: MembershipItemId, nextItemId: MembershipItemId) => void;
  onCancelSuspensionRequest?: (subscriptionCode: string, subscriptionSuspensionCode: string) => Promise<void>;
  onResumeSubscriptionRequest?: (subscriptionCode: string) => void;
};

export const SubscriptionSummaryView = ({
  summary,
  isWithdrawalSet,
  onUpdateRequest,
  onUpdateCancelRequest,
  onCancelSuspensionRequest,
  onResumeSubscriptionRequest,
}: Props) => {
  const {
    subscribed,
    next_period_membership_item,
    next_period_membership_item_id,
    current_period_membership_item_id,
    suspended,
    future_suspension,
    subscription_suspension,
    next_payment_date,
    plan_item_price,
    plan_item_price_with_tax,
    chain_id,
    plan_name,
    starts_at,
    strict_expires_at,
    subscription_code,
    current_period_starts_at,
    next_period_starts_at,
    plan_key,
  } = summary;
  const config = usePlanOperationConfig();
  // プラン開始前だったら true、プランが開始していたら false
  const future = moment(starts_at).isAfter(moment());
  // プラン変更を予約中かどうか
  const hasFutureChangePlan =
    next_period_membership_item != null && next_period_membership_item_id != current_period_membership_item_id;
  // 休会中かどうか
  const isSuspended = subscribed && suspended;
  // 休会予約が存在しているかどうか
  const hasFutureSuspension = subscribed && subscription_suspension && future_suspension;
  // 退会、休会、プラン変更の予約が存在せず、プラン変更または休会手続きが可能かどうか
  const canChangePlanOrSuspendPlan =
    subscribed &&
    !isSuspended &&
    !isWithdrawalSet &&
    !hasFutureChangePlan &&
    !hasFutureSuspension &&
    config.planUpdateEnabled;
  // 次回の支払い予定があるかどうか
  const hasNextPayment =
    subscribed &&
    !isWithdrawalSet &&
    !hasFutureChangePlan &&
    !hasFutureSuspension &&
    next_payment_date &&
    plan_item_price > 0;

  const isSp = GlobalStatesV3.viewport.isSp;
  const lessonToken = GlobalStatesV3.currentUser.availableTokens.findAffordableToken(
    TokenType.ONLINE_LESSON,
    1,
    moment()
  );
  const lessonTokenAmount = lessonToken && lessonToken.amount;
  // ライトプランのレッスン消費可能数
  const MAX_LESSON_COUNT = 5;
  const lessonConsumption = `${lessonTokenAmount}/${MAX_LESSON_COUNT}`;
  return (
    <>
      <div
        styleName="subscription-summary"
        className="subscription-summary-view"
        id={`subscription-summary-view-${chain_id}`}
      >
        {future ? (
          <div>
            <SubscriptionSummaryRow title={<span>ご利用中のプラン</span>}>
              <div data-value="current-plan" styleName="current-plan">
                {plan_name}
              </div>
            </SubscriptionSummaryRow>
            <SubscriptionSummaryRow title="受講開始日" className="start-date-info">
              <DetailedDate2 date={starts_at} />
            </SubscriptionSummaryRow>
          </div>
        ) : (
          <div>
            <SubscriptionSummaryRow title={<span>ご利用中のプラン</span>}>
              <div data-value="current-plan" styleName="current-plan">
                {plan_name}
                {isSuspended && (
                  <div>
                    <span styleName="suspension-status">休会中</span>
                  </div>
                )}
                {isSuspended && config.planUpdateEnabled && (
                  <div styleName="action-area">
                    {isSuspended && (
                      <Button
                        size="small"
                        onClick={() =>
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionCancelInfo(subscription_code!))
                        }
                        fullWidth={isSp}
                      >
                        復帰する
                      </Button>
                    )}
                    {!isSuspended && (
                      <Button
                        size="small"
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => onResumeSubscriptionRequest && onResumeSubscriptionRequest(subscription_code!)}
                        fullWidth={isSp}
                      >
                        いますぐ復帰する
                      </Button>
                    )}
                  </div>
                )}
                {canChangePlanOrSuspendPlan && (
                  <div styleName="action-area">
                    <Button
                      size="small"
                      className="plan-update-request-button"
                      onClick={() => onUpdateRequest && onUpdateRequest(current_period_membership_item_id)}
                      fullWidth={isSp}
                    >
                      プランを変更する
                    </Button>
                    {/* テキストリンクタイプのボタンはまだshelikes-design-systemにないのでこれまでのButtonコンポーネントを使用する */}
                    <OldButton
                      type="underline"
                      size="s"
                      className="suspension-request-button"
                      styleName="suspension-button"
                      onClick={() =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        GlobalStatesV3.routing.push(V3Routes.subscriptionSuspensionInfo(subscription_code!))
                      }
                    >
                      休会手続きに進む
                    </OldButton>
                  </div>
                )}
              </div>
              {!subscribed && <div styleName="attention">※こちらのプランは休会・退会ができません。</div>}
            </SubscriptionSummaryRow>
            {hasFutureSuspension && (
              <SubscriptionSummaryRow title="休会予約" className="suspension-schedule-info">
                <div>
                  <span styleName="suspension-period">
                    <DetailedDateRange
                      fromDate={subscription_suspension.starts_at}
                      toDate={subscription_suspension.ends_at}
                    />
                  </span>
                  での休会が予約されています
                </div>
                {config.planUpdateEnabled && (
                  <div styleName="cancel-future-suspension">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        onCancelSuspensionRequest &&
                        onCancelSuspensionRequest(
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          subscription_code!,
                          subscription_suspension.subscription_suspension_code
                        )
                      }
                      fullWidth={isSp}
                    >
                      休会予約を取り消す
                    </Button>
                  </div>
                )}
              </SubscriptionSummaryRow>
            )}
            <SubscriptionSummaryRow title="現プランの受講可能期間" className="subscription-period-info">
              {strict_expires_at && current_period_starts_at && (
                <DetailedPeriod startsAt={current_period_starts_at} expiresAt={strict_expires_at} />
              )}
              {!strict_expires_at && <DetailedDate2 date={current_period_starts_at} />}
            </SubscriptionSummaryRow>
            {lessonToken && plan_key == 'casual' && lessonTokenAmount && lessonTokenAmount >= 0 && (
              <SubscriptionSummaryRow title="残りレッスン数" className="subscription-lesson-info">
                <>
                  {lessonConsumption}レッスン
                  <span styleName="expires-at-date">
                    （利用期限：〜{moment(lessonToken.expires_at).add('days', -1).format('YYYY年MM月DD日')}）
                  </span>
                </>
              </SubscriptionSummaryRow>
            )}
            {isSuspended && (
              <SubscriptionSummaryRow title="休会期間" className="suspension-info">
                <DetailedPeriod
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  startsAt={subscription_suspension!.starts_at}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  expiresAt={subscription_suspension!.ends_at}
                />
              </SubscriptionSummaryRow>
            )}
            {hasNextPayment && (
              <SubscriptionSummaryRow title="次回プラン更新" className="next-payment-info">
                <div>
                  <DetailedDate2 date={next_payment_date} />に{plan_item_price_with_tax.toLocaleString()}
                  円（税込）
                  <SpBr />
                  を請求予定です。
                  <br />
                  <span styleName="red">※プランは自動更新となります</span>
                </div>
              </SubscriptionSummaryRow>
            )}
            {hasFutureChangePlan && (
              <SubscriptionSummaryRow title="プラン変更予約">
                <div styleName="update-schedule">
                  <div>
                    <b>
                      <DetailedDate2 date={next_period_starts_at} />
                    </b>
                    に<b>{next_period_membership_item.name}</b>
                    <MobBr />
                    への変更が予約されています。
                  </div>
                  {/* NOTE: money_regular の場合は「変更を取り消す」ボタンを表示しない
                            全ての money_regular 会員が money_standard へ移行された後にこの条件は削除できる見込み */}
                  {plan_key !== 'money_regular' && (
                    <div id="cancel-plan-change-button" styleName="button-wrapper">
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() =>
                          onUpdateCancelRequest &&
                          onUpdateCancelRequest(current_period_membership_item_id, next_period_membership_item_id)
                        }
                        fullWidth={isSp}
                      >
                        変更を取り消す
                      </Button>
                    </div>
                  )}
                </div>
              </SubscriptionSummaryRow>
            )}
          </div>
        )}
      </div>
    </>
  );
};
